import { FieldArray, FieldArrayRenderProps } from 'formik'
import { assoc, find, findIndex, propEq } from 'ramda'
import { Box } from '@machobear/design-system'

import FeatureItem from './feature-item'
import { FormSection } from '../../components'
import { DetailsFormValues, Feature } from '../../types'

interface FeatureListProps {
  name: 'unit_features' | 'listing_utilities'
  label: string
  tooltip?: React.ReactNode
  options: Feature[]
  typeOptions?: string[]
  formValues: DetailsFormValues
  defaultType?: string
  translationKeyBase?: 'unit_features' | 'utilities'
}

const FeatureList = ({
  name,
  label,
  tooltip,
  options,
  typeOptions = ['private', 'shared'],
  formValues,
  defaultType = 'private',
  translationKeyBase = 'unit_features'
}: FeatureListProps) => {
  const featureValues = formValues[name] ?? []
  const unitScope = formValues.unit_type_scope_txt_id
  const showTypeOptions = unitScope !== 'entire'

  const getCurrentType = (option: Feature) =>
    find(propEq('txt_id', option.txt_id), featureValues)?.type || defaultType

  const handleCheckboxChange = (
    isChecked: boolean,
    option: Feature,
    arrayHelpers: FieldArrayRenderProps
  ) => {
    if (isChecked) {
      arrayHelpers.push({ name: option.name, txt_id: option.txt_id, type: defaultType })
    } else {
      const idx = findIndex(propEq('txt_id', option.txt_id), featureValues)
      arrayHelpers.remove(idx)
    }
  }

  const handleTypeChange = (
    value: string,
    option: Feature,
    arrayHelpers: FieldArrayRenderProps
  ) => {
    const idx = findIndex(propEq('txt_id', option.txt_id), featureValues)
    const newVal = assoc('type', value, featureValues[idx])
    arrayHelpers.replace(idx, newVal)
  }

  return (
    <FormSection label={label} tooltip={tooltip}>
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <Box
            display="flex"
            flexDirection="column"
            borderRadius="small"
            border="borderNeutralLight"
            role="listbox"
          >
            {options.map((option, index) => {
              const checked = featureValues.some(f => f.txt_id === option.txt_id)
              const currentType = getCurrentType(option)
              const isLast = index >= options.length - 1

              return (
                <FeatureItem
                  key={`${option.txt_id}-${index}`}
                  option={option}
                  isLast={isLast}
                  checked={checked}
                  currentType={currentType}
                  showTypeOptions={showTypeOptions}
                  typeOptions={typeOptions}
                  translationKeyBase={translationKeyBase}
                  onCheckboxChange={handleCheckboxChange}
                  onTypeChange={handleTypeChange}
                  arrayHelpers={arrayHelpers}
                />
              )
            })}
          </Box>
        )}
      />
    </FormSection>
  )
}

export default FeatureList
